/**
 * T-048: per-endpoint rate-limit overrides UI for the Pocket-relevant
 * endpoint types.
 *
 * Reads via GET /admin/pocket-settings/rate-limits, edits via PUT
 * (superadmin only). Non-superadmins see read-only rows with the
 * 24h 429 count.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'react-toastify';
import { useRBAC } from '@/contexts/RBACContext';
import { getRateLimits, updateRateLimit } from '@/lib/pocketSettingsApi';
import type { RateLimitEntry } from '@/types/pocketSettings';

export function RateLimitOverridesCard() {
  const { isSuperadmin } = useRBAC();
  const [entries, setEntries] = useState<RateLimitEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingType, setEditingType] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ window_seconds: string; max_requests: string; enabled: boolean } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRateLimits()
      .then((data) => {
        if (!cancelled) setEntries(data.entries || []);
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load rate limits.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  function startEdit(entry: RateLimitEntry) {
    setEditingType(entry.endpoint_type);
    setDraft({
      window_seconds: String(entry.window_seconds),
      max_requests: String(entry.max_requests),
      enabled: entry.enabled,
    });
  }

  function cancelEdit() {
    setEditingType(null);
    setDraft(null);
  }

  async function saveEdit() {
    if (!editingType || !draft) return;
    const window_seconds = Number(draft.window_seconds);
    const max_requests = Number(draft.max_requests);
    if (
      !Number.isInteger(window_seconds) ||
      window_seconds < 1 ||
      window_seconds > 3600 ||
      !Number.isInteger(max_requests) ||
      max_requests < 1 ||
      max_requests > 1000
    ) {
      toast.error('Window must be 1–3600s. Max requests must be 1–1000.');
      return;
    }
    setSubmitting(true);
    try {
      await updateRateLimit(editingType, {
        window_seconds,
        max_requests,
        enabled: draft.enabled,
      });
      toast.success(`Saved ${editingType}.`);
      cancelEdit();
      refresh();
    } catch {
      toast.error('Save failed.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-semibold">Rate-limit overrides</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Per-user request budgets. Tighter = abuse protection, looser = lets
          legit bursts through. Watch the <span className="text-foreground font-medium">24h 429s</span> column —
          non-zero means real users hit the wall.
        </p>
        {error ? <p className="text-sm text-destructive mb-4">{error}</p> : null}
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : entries.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No Pocket-relevant endpoint types configured.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Endpoint type</TableHead>
                <TableHead>Window</TableHead>
                <TableHead>Max</TableHead>
                <TableHead>Enabled</TableHead>
                <TableHead>24h 429s</TableHead>
                {isSuperadmin ? <TableHead className="text-right">Actions</TableHead> : null}
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry) => {
                const editing = editingType === entry.endpoint_type;
                return (
                  <TableRow key={entry.endpoint_type}>
                    <TableCell className="font-mono text-xs">
                      {entry.endpoint_type}
                      {entry.is_default ? (
                        <Badge variant="outline" className="ml-2 text-xs">
                          default
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {editing ? (
                        <Input
                          type="number"
                          min={1}
                          max={3600}
                          value={draft?.window_seconds ?? ''}
                          onChange={(e) =>
                            setDraft((d) => (d ? { ...d, window_seconds: e.target.value } : d))
                          }
                          className="w-24"
                          aria-label={`window_seconds for ${entry.endpoint_type}`}
                        />
                      ) : (
                        `${entry.window_seconds}s`
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {editing ? (
                        <Input
                          type="number"
                          min={1}
                          max={1000}
                          value={draft?.max_requests ?? ''}
                          onChange={(e) =>
                            setDraft((d) => (d ? { ...d, max_requests: e.target.value } : d))
                          }
                          className="w-24"
                          aria-label={`max_requests for ${entry.endpoint_type}`}
                        />
                      ) : (
                        entry.max_requests
                      )}
                    </TableCell>
                    <TableCell>
                      {editing ? (
                        <input
                          type="checkbox"
                          checked={draft?.enabled ?? true}
                          onChange={(e) =>
                            setDraft((d) => (d ? { ...d, enabled: e.target.checked } : d))
                          }
                          aria-label={`enabled for ${entry.endpoint_type}`}
                        />
                      ) : entry.enabled ? (
                        <Badge variant="secondary">on</Badge>
                      ) : (
                        <Badge variant="outline">off</Badge>
                      )}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {entry.blocked_24h ?? 0}
                    </TableCell>
                    {isSuperadmin ? (
                      <TableCell className="text-right space-x-2">
                        {editing ? (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEdit}
                              disabled={submitting}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              onClick={saveEdit}
                              disabled={submitting}
                            >
                              {submitting ? 'Saving…' : 'Save'}
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEdit(entry)}
                            disabled={editingType !== null}
                          >
                            Edit
                          </Button>
                        )}
                      </TableCell>
                    ) : null}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        <p className="mt-3 text-xs text-muted-foreground">
          <strong>After saving:</strong> the new limit takes effect within ~5
          seconds (rate-limiter config cache TTL). Disabling a row turns the
          limit off entirely for that endpoint type — leave it on unless
          you're debugging a 429 false-positive in real time. Every change
          is audited.
        </p>
      </CardContent>
    </Card>
  );
}
